import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Save } from 'lucide-react';
import api from '../../../services/api';
import { useI18n } from '../../../i18n/I18nProvider';
import { errorText } from '../../../lib/errors';
import { Card, CardHeader, CardBody, Field, Input, Textarea, Select, Button } from '../../../ui';

const FIELDS = ['name', 'company_code', 'legal_name', 'edrpou', 'contact_name', 'contact_phone', 'contact_email', 'city', 'address', 'locations_count', 'notes', 'timezone', 'wallet_currency'];
// Часовые пояса, в которых вероятны клиенты; текущий пояс компании добавляется, даже если его нет в списке
const TIMEZONES = ['Europe/Kyiv', 'Europe/Warsaw', 'Europe/Berlin', 'Europe/London', 'Europe/Prague', 'Europe/Vilnius', 'Europe/Riga', 'Europe/Tallinn', 'Europe/Chisinau', 'Europe/Bucharest', 'Asia/Tbilisi', 'Asia/Almaty', 'Asia/Dubai', 'America/New_York'];
const WALLET_CURRENCIES = ['UAH', 'USD', 'EUR', 'PLN', 'GBP', 'CZK', 'GEL', 'KZT', 'MDL'];

const DEFAULTS = { locations_count: 1, timezone: 'Europe/Kyiv', wallet_currency: 'UAH' };
const fromTenant = (tenant) => Object.fromEntries(FIELDS.map((f) => [f, tenant[f] ?? DEFAULTS[f] ?? '']));

/** Изменённые поля формы относительно исходных значений */
export function changedFields(form, initial) {
  return Object.fromEntries(Object.entries(form).filter(([k, v]) => String(v ?? '') !== String(initial[k] ?? '')));
}

export default function ProfileTab({ tenant, onSaved }) {
  const { t } = useI18n();
  const initial = useMemo(() => fromTenant(tenant), [tenant]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(initial); }, [initial]);

  const changes = changedFields(form, initial);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!Object.keys(changes).length) return;
    setSaving(true);
    try {
      const payload = { ...changes };
      if ('locations_count' in payload) payload.locations_count = Number(payload.locations_count) || 1;
      await api.put(`/admin/tenants/${tenant.id}`, payload);
      toast.success(t('common.saved'));
      onSaved();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-4xl space-y-6">
      <Card>
        <CardHeader title={t('profile.general')} />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Field label={t('profile.name')} required>
            <Input value={form.name} onChange={set('name')} required maxLength={100} />
          </Field>
          <Field label={t('companies.companyCode')} hint={t('companies.companyCodeEditHint')}>
            <Input
              value={form.company_code}
              onChange={(e) => setForm({ ...form, company_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
              maxLength={20}
              minLength={3}
              className="font-mono"
            />
          </Field>
          <Field label={t('profile.locations')}>
            <Input type="number" min="1" value={form.locations_count} onChange={set('locations_count')} className="tabular" />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('profile.regional')} description={t('profile.regionalHint')} />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Field label={t('profile.timezone')} hint={t('profile.timezoneHint')}>
            <Select value={form.timezone} onChange={set('timezone')}>
              {[...new Set([form.timezone, ...TIMEZONES])].map((tz) => <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>)}
            </Select>
          </Field>
          <Field label={t('profile.walletCurrency')} hint={t('profile.walletCurrencyHint')}>
            <Select value={form.wallet_currency} onChange={set('wallet_currency')}>
              {WALLET_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('profile.legal')} />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Field label={t('profile.legalName')}>
            <Input value={form.legal_name} onChange={set('legal_name')} maxLength={200} />
          </Field>
          <Field label={t('profile.edrpou')}>
            <Input value={form.edrpou} onChange={set('edrpou')} maxLength={50} className="font-mono" />
          </Field>
          <Field label={t('profile.city')}>
            <Input value={form.city} onChange={set('city')} maxLength={100} />
          </Field>
          <Field label={t('profile.address')}>
            <Input value={form.address} onChange={set('address')} maxLength={200} />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('profile.contacts')} />
        <CardBody className="grid gap-4 sm:grid-cols-3">
          <Field label={t('profile.contactName')}>
            <Input value={form.contact_name} onChange={set('contact_name')} maxLength={100} />
          </Field>
          <Field label={t('profile.contactPhone')}>
            <Input type="tel" value={form.contact_phone} onChange={set('contact_phone')} maxLength={50} />
          </Field>
          <Field label={t('profile.contactEmail')}>
            <Input type="email" value={form.contact_email} onChange={set('contact_email')} />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('profile.notes')} description={t('profile.notesHint')} />
        <CardBody>
          <Textarea value={form.notes} onChange={set('notes')} rows={4} maxLength={2000} aria-label={t('profile.notes')} />
        </CardBody>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="secondary" disabled={!Object.keys(changes).length || saving} onClick={() => setForm(initial)}>{t('common.cancel')}</Button>
        <Button type="submit" icon={Save} loading={saving} disabled={!Object.keys(changes).length}>{t('common.save')}</Button>
      </div>
    </form>
  );
}
